/**
 * DashboardContext - Gestione centralizzata dello stato della Dashboard
 * Elimina il props drilling passando dati attraverso Context
 */
import React, { createContext, useContext, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

const DashboardContext = createContext(null)

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))
const MAX_RETRIES = 3

export const DashboardProvider = ({ children }) => {
  const navigate = useNavigate()

  // Stato utente
  const [user, setUser] = useState(null)

  // Dati principali
  const [programs, setPrograms] = useState([])
  const [topics, setTopics] = useState([])
  const [qualifications, setQualifications] = useState([])
  const [people, setPeople] = useState([])
  const [companies, setCompanies] = useState([])
  const [schedules, setSchedules] = useState([])
  const [editorials, setEditorials] = useState([])
  const [rubrica, setRubrica] = useState([])

  // Stato UI
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Fetch di tutti i dati della dashboard
   */
  const fetchAllData = useCallback(async () => {
    if (!localStorage.getItem('accessToken')) {
      navigate('/login')
      return false
    }

    setIsLoading(true)
    setError(null)

    // Fetch utente corrente
    try {
      const me = await api.get('/auth/me')
      setUser(me.data.user)
    } catch (err) {
      console.error('Non autenticato, torno al login', err)
      navigate('/login')
      return false
    }

    // Fetch dati con retry
    let retries = 0
    while (localStorage.getItem('accessToken') && retries < MAX_RETRIES) {
      try {
        const results = await Promise.allSettled([
          api.get('/programs'),
          api.get('/topics'),
          api.get('/qualifications'),
          api.get('/people'),
          api.get('/schedules'),
          api.get('/companies'),
          api.get('/editorials'),
          api.get('/rubrica'),
        ])

        // Gestione partial failures
        setPrograms(results[0].status === 'fulfilled' ? results[0].value.data : [])
        setTopics(results[1].status === 'fulfilled' ? results[1].value.data : [])
        setQualifications(results[2].status === 'fulfilled' ? results[2].value.data : [])
        setPeople(results[3].status === 'fulfilled' ? results[3].value.data : [])
        setSchedules(results[4].status === 'fulfilled' ? results[4].value.data : [])
        setCompanies(results[5].status === 'fulfilled' ? results[5].value.data : [])
        setEditorials(results[6].status === 'fulfilled' ? results[6].value.data : [])
        setRubrica(results[7].status === 'fulfilled' ? results[7].value.data : [])

        // Check API critiche
        const criticalOk = results[0].status === 'fulfilled' && results[4].status === 'fulfilled'
        if (criticalOk) {
          break
        }

        // Retry se API critiche fallite
        const failedApis = results.filter(r => r.status === 'rejected')
        console.warn(`${failedApis.length} API fallite, retry ${retries + 1}/${MAX_RETRIES}`)
        retries++
        if (retries < MAX_RETRIES) {
          await delay(2000 * retries)
        }
      } catch (error) {
        console.error('Errore nel recupero dati dashboard:', error)
        if (error.response?.status === 401) {
          navigate('/login')
          break
        }
        retries++
        if (retries < MAX_RETRIES) {
          await delay(2000 * retries)
        }
      }
    }

    if (retries >= MAX_RETRIES) {
      setError('Impossibile caricare i dati. Riprova più tardi.')
    }

    setIsLoading(false)
    return true
  }, [navigate])

  /**
   * Refresh singola risorsa
   */
  const refreshResource = useCallback(async (resource) => {
    try {
      const res = await api.get(`/${resource}`)
      switch (resource) {
        case 'programs':
          setPrograms(res.data)
          break
        case 'topics':
          setTopics(res.data)
          break
        case 'qualifications':
          setQualifications(res.data)
          break
        case 'people':
          setPeople(res.data)
          break
        case 'schedules':
          setSchedules(res.data)
          break
        case 'companies':
          setCompanies(res.data)
          break
        case 'editorials':
          setEditorials(res.data)
          break
        case 'rubrica':
          setRubrica(res.data)
          break
        default:
          break
      }
      return true
    } catch (err) {
      console.error(`Errore refresh ${resource}:`, err)
      return false
    }
  }, [])

  /**
   * Verifica se l'utente ha un ruolo specifico
   */
  const hasRole = useCallback((roleName) => {
    return user?.roles?.some(r => r.name === roleName) ?? false
  }, [user])

  /**
   * Verifica se l'utente è admin
   */
  const isAdmin = useCallback(() => {
    return hasRole('admin')
  }, [hasRole])

  // Valore del context
  const value = {
    // Stato
    user,
    programs,
    topics,
    qualifications,
    people,
    companies,
    schedules,
    editorials,
    rubrica,
    isLoading,
    error,

    // Setters (per update locali)
    setPrograms,
    setTopics,
    setQualifications,
    setPeople,
    setCompanies,
    setSchedules,
    setEditorials,
    setRubrica,

    // Actions
    fetchAllData,
    refreshResource,
    refreshAll: fetchAllData,

    // Helpers
    hasRole,
    isAdmin,
  }

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}

/**
 * Hook per usare il DashboardContext
 */
export const useDashboard = () => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboard deve essere usato dentro DashboardProvider')
  }
  return context
}

/**
 * Hook per ottenere solo i dati (senza setters)
 */
export const useDashboardData = () => {
  const { programs, topics, qualifications, people, companies, schedules, editorials, rubrica, user } = useDashboard()
  return { programs, topics, qualifications, people, companies, schedules, editorials, rubrica, user }
}

/**
 * Hook per le liste (dropdown, select)
 */
export const useLists = () => {
  const { people, topics, qualifications, companies } = useDashboard()
  return { peopleList: people, topicList: topics, qualificationList: qualifications, companyList: companies }
}

export default DashboardContext
